import { useTranslation } from "react-i18next";
import * as yup from "yup";
import useAnsQuesValidationSchema from "./useAnsQuesValidationSchema";
import i18n from "i18next";

function checkImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
}

function checkVideo(url) {
  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.onloadedmetadata = () => resolve(true);
    video.onerror = () => resolve(false);
    video.src = url;
  });
}

const hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

function useEventValidationSchema() {
  const { t } = useTranslation("validationsSchema");
  const supportedLanguages = i18n?.options?.supportedLngs?.filter(
    (language) => language !== "cimode",
  );
  const answerQuestionValidationSchema = useAnsQuesValidationSchema();

  const schema = yup.object({
    name: yup.string().required(t("eventNameCannotBeEmpty")),
    descriptions: yup
      .object()
      .required(t("descriptionCannotBeEmpty"))
      .test(
        "valid-descriptions",
        t("descriptionCannotBeEmpty"),
        (descriptions) =>
          descriptions &&
          Object.keys(descriptions).length > 0 &&
          Object.entries(descriptions).every(
            ([key, val]) => supportedLanguages?.includes(key) && val?.trim() !== "",
          ),
      ),

    endDate: yup.date().required(t("endDateCannotBeEmpty")).typeError(t("endDateCannotBeEmpty")),
    eventFromDate: yup
      .date()
      .required(t("startDateCannotBeEmpty"))
      .typeError(t("startDateCannotBeEmpty")),
    eventToDate: yup
      .date()
      .required(t("eventToDateCannotBeEmpty"))
      .typeError(t("eventToDateCannotBeEmpty")),
    mediaIsVideo: yup.boolean().required(),
    mediaUrl: yup
      .string()
      .required(t("imageUrlCannotBeEmpty"))
      .test("is-valid-media", function (value) {
        if (!value) return false;

        if (this.parent.mediaIsVideo) {
          return checkVideo(value).then((isValid) => {
            if (!isValid) {
              return this.createError({ message: t("invalidVideoUrl") });
            }
            return true;
          });
        } else {
          return checkImage(value).then((isValid) => {
            if (!isValid) {
              return this.createError({ message: t("invalidImageUrl") });
            }
            return true;
          });
        }
      }),
    formFields: yup.array().of(
      yup.lazy((value) => {
        if (typeof value === "string") {
          return yup.string().required();
        }
        return yup.object().shape({
          value: yup.string().required(),
          isMandatory: yup.boolean().required(),
        });
      }),
    ),
    open: yup.boolean().required(t("openFieldMustBeTrueOrFalse")),
    ansQues: yup.array().of(answerQuestionValidationSchema),
    subdivision: yup.boolean(),
    logoUrl: yup
      .string()
      .nullable()
      .test("is-image-url", t("invalidLogoUrl"), async (value) => {
        if (!value) return true;
        return await checkImage(value);
      }),

    darkMode: yup.boolean(),
    mainColor1: yup.string().matches(hexColorRegex, t("wrongHexa")).nullable(),
    mainColor2: yup.string().matches(hexColorRegex, t("wrongHexa")).nullable(),
    maxEntries: yup
      .number()
      .moreThan(0, t("moreThanZero"))
      .test((value) => value === null || Number.isInteger(value))
      .nullable(),
    formRequiredOnSite: yup.boolean(),
    feeDelegated: yup.boolean(),
  });

  return schema;
}

export default useEventValidationSchema;
